import { Injectable } from '@angular/core';
import { Configuration, GroupsService } from '../../api-client';
import { HttpClient } from '@angular/common/http';
import { Group, GroupSlot } from '../../models';
import { map, Observable } from 'rxjs';
import { LoggingService } from '../shared/logging/logging.service';

@Injectable({
  providedIn: 'root',
})
export class GroupService extends GroupsService {
  constructor(httpClient: HttpClient, config: Configuration, private loggingService: LoggingService) {
    super(httpClient, config.basePath!, config);
  }

  /**
   * Get all groups from API
   * @param includeSlots Optional: include slots in the response
   */
  getAll(includeSlots: boolean = true): Observable<Group[]> {
    this.loggingService.debug(`GroupService.getAll called, includeSlots: ${includeSlots}`, 'GroupService');

    this.loggingService.debug('GroupService.getAll: Making API request to fetch groups', 'GroupService');
    return super
      .getGroupsEndpoint(
        undefined,
        undefined,
        undefined,
        includeSlots ? 'slots' : undefined
      )
      .pipe(
        map((groups: Group[]) =>
          groups.map((group) => ({
            ...group,
          }))
        )
      );
  }

  /**
   * Get group by ID
   */
  getById(groupId: string): Observable<Group | undefined> {
    return this.getAll().pipe(
      map((groups) => groups.find((group) => group.groupId === groupId))
    );
  }

  /**
   * Get groups filtered by tournament, round and course
   */
  getByTournamentRoundCourse(
    tournamentId: string,
    roundId: string,
    courseId: string
  ): Observable<Group[]> {
    this.loggingService.debug(`GroupService.getByTournamentRoundCourse called with tournamentId: ${tournamentId}, roundId: ${roundId}, courseId: ${courseId}`, 'GroupService');

    return this.getAll().pipe(
      map((groups) => {
        const filteredGroups = groups.filter((group) => {
          const matchTournament = group.tournamentId === tournamentId;
          const matchRound = group.roundId === roundId;
          const matchCourse = group.courseId === courseId;

          return matchTournament && matchRound && matchCourse;
        });

        this.loggingService.debug(
          `GroupService: Filtered to ${filteredGroups.length} matching groups`,
          'GroupService'
        );

        return filteredGroups;
      })
    );
  }

  /**
   * Get group slots from API
   */
  getGroupSlots(groupId: string): Observable<GroupSlot[]> {
    return super.getGroupSlotsEndpoint(groupId);
  }

  /**
   * Get a group by ID directly from the API
   */
  getByIdDirect(groupId: string): Observable<Group> {
    return super.getGroupByIdEndpoint(groupId);
  }
}
